import React, { useEffect, useRef, useState } from "react";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { api, apiError } from "../api";
import { AREA_META } from "../constants/areaMeta";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { MarkdownLite } from "../components/MarkdownLite";
import { RiseLogo } from "../components/RiseLogo";
import { InviteCodeBar } from "../components/InviteCodeBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Users, Activity, TrendingUp, FileText, Star, CheckCircle2, ShieldCheck, ChevronDown, Stethoscope, Filter, X, Sparkles, AlertTriangle, RefreshCw, Building2, Plus, Download, Loader2, Pencil } from "lucide-react";

export default function AdminDashboard() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState(null);
  const [openUser, setOpenUser] = useState(null);
  const [openReport, setOpenReport] = useState(null);
  const [filters, setFilters] = useState({ min_age: "", max_age: "", pathology: "" });
  const [consults, setConsults] = useState([]);
  const [clinical, setClinical] = useState({});
  const [tenants, setTenants] = useState([]);
  const [scoped, setScoped] = useState(false);
  const [tenant, setTenant] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editSlug, setEditSlug] = useState(null);
  const [addForm, setAddForm] = useState({ name: "", slug: "", logo_url: "", accent_color: "#3F6E85", welcome_message: "", is_active: true });
  const [addBusy, setAddBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [compare, setCompare] = useState([]);
  const pdfRef = useRef(null);

  const RESERVED = ["about", "contact", "terms", "privacy", "login", "register", "forgot-password", "reset-password", "onboarding", "dashboard", "track", "diary", "program", "summary", "reports", "devices", "profile", "protocols", "learn", "admin", "api", "static", "rise", "tenants", "auth"];
  const slugReserved = !editSlug && RESERVED.includes((addForm.slug || "").toLowerCase());
  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 40);

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { toast.error(t("admin_logo_too_big")); return; }
    const reader = new FileReader();
    reader.onload = () => setAddForm((f) => ({ ...f, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const openCreate = () => {
    setEditSlug(null);
    setAddForm({ name: "", slug: "", logo_url: "", accent_color: "#3F6E85", welcome_message: "", is_active: true });
    setAddOpen(true);
  };

  const openEdit = (tn) => {
    setEditSlug(tn.slug);
    setAddForm({
      name: tn.name || "", slug: tn.slug, logo_url: tn.logo_url || "",
      accent_color: tn.accent_color || "#3F6E85", welcome_message: tn.welcome_message || "",
      is_active: tn.is_active !== false,
    });
    setAddOpen(true);
  };

  const refreshTenants = async () => {
    const list = await api.get("/admin/tenants");
    setTenants(list.data.tenants || []);
    if (!list.data.scoped) api.get("/admin/compare").then((r) => setCompare(r.data.companies || [])).catch(() => {});
  };

  const saveTenant = async () => {
    setAddBusy(true);
    try {
      if (editSlug) {
        await api.put(`/admin/tenants/${editSlug}`, {
          name: addForm.name.trim(), logo_url: addForm.logo_url,
          accent_color: addForm.accent_color, welcome_message: addForm.welcome_message,
          is_active: addForm.is_active,
        });
        toast.success(t("admin_company_updated"));
        await refreshTenants();
      } else {
        if (RESERVED.includes((addForm.slug || slugify(addForm.name)).toLowerCase())) {
          toast.error(t("admin_slug_reserved"));
          return;
        }
        const payload = {
          name: addForm.name.trim(), slug: (addForm.slug || slugify(addForm.name)),
          logo_url: addForm.logo_url, accent_color: addForm.accent_color, welcome_message: addForm.welcome_message,
        };
        const r = await api.post("/admin/tenants", payload);
        toast.success(t("admin_company_created"));
        await refreshTenants();
        setTenant(r.data.tenant.slug);
      }
      setAddOpen(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setAddBusy(false);
    }
  };

  const downloadCompanyPdf = async () => {
    if (!pdfRef.current) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(pdfRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, Math.min(h, pdf.internal.pageSize.getHeight()));
      pdf.save(`RISE_${(currentTenant?.name || tenant || "azienda").replace(/\s+/g, "_")}_report.pdf`);
    } catch (e) {
      toast.error("PDF error");
    } finally {
      setPdfBusy(false);
    }
  };

  const loadClinical = async (uid, refresh = false) => {
    setClinical((c) => ({ ...c, [uid]: { ...(c[uid] || {}), loading: true } }));
    try {
      let r = await api.get(`/admin/user/${uid}/clinical-summary`, { params: { lang, ...(refresh ? { refresh: true } : {}) } });
      if (!r.data.summary && !refresh) r = await api.get(`/admin/user/${uid}/clinical-summary`, { params: { lang, refresh: true } });
      setClinical((c) => ({ ...c, [uid]: { loading: false, content: r.data.summary, generated_at: r.data.generated_at } }));
    } catch (e) {
      setClinical((c) => ({ ...c, [uid]: { loading: false, error: true } }));
    }
  };

  const loadUsers = (f = filters, tn = tenant) => {
    if (scoped) return;
    const params = {};
    if (tn) params.tenant = tn;
    if (f.min_age !== "") params.min_age = f.min_age;
    if (f.max_age !== "") params.max_age = f.max_age;
    if (f.pathology.trim()) params.pathology = f.pathology.trim();
    api.get("/admin/user-reports", { params }).then((r) => setUsers(r.data.users)).catch(() => {});
  };

  // load tenant list first and select the default one
  useEffect(() => {
    api.get("/admin/tenants").then((r) => {
      const list = r.data.tenants || [];
      setTenants(list);
      setScoped(!!r.data.scoped);
      if (list.length) setTenant(list[0].slug);
      if (!r.data.scoped) api.get("/admin/compare").then((c) => setCompare(c.data.companies || [])).catch(() => {});
    }).catch(() => {});
  }, []);

  // (re)load all admin data whenever the selected tenant changes
  useEffect(() => {
    if (!tenant) return;
    setData(null);
    setUsers(null);
    setOpenUser(null);
    setClinical({});
    api.get("/admin/overview", { params: { tenant } }).then((r) => setData(r.data)).catch(() => {});
    if (!scoped) {
      api.get("/admin/consultations", { params: { tenant } }).then((r) => setConsults(r.data.consultations || [])).catch(() => {});
      loadUsers(filters, tenant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  const resetFilters = () => {
    const cleared = { min_age: "", max_age: "", pathology: "" };
    setFilters(cleared);
    loadUsers(cleared, tenant);
  };

  const currentTenant = tenants.find((x) => x.slug === tenant);

  const chartData = data && !data.below_threshold
    ? Object.keys(AREA_META).map((a) => ({ name: t(a), value: data.area_participation[a], color: AREA_META[a].color }))
    : [];

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2">{t("admin_dashboard")}</h1>
        <p className="flex items-center gap-2 text-sm text-[#3B604E] bg-[#EAF0EC] rounded-full px-4 py-2 w-fit mb-6" data-testid="admin-privacy-note">
          <ShieldCheck className="w-4 h-4" /> {t("admin_note")}
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="admin-tenant-bar">
          <Building2 className="w-5 h-5 text-[#3F6E85]" />
          <span className="text-sm font-medium text-[#6E6B65]">{t("admin_company")}:</span>
          {scoped ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-[#3F6E85] text-white text-sm font-semibold px-4 py-1.5" data-testid="admin-tenant-locked">
              {currentTenant?.logo_url && <img src={currentTenant.logo_url} alt={currentTenant.name} className="h-4 object-contain bg-white rounded px-1" />}
              {currentTenant?.name || tenant}
            </span>
          ) : (
            <div className="inline-flex rounded-full bg-[#F3F1ED] p-1" data-testid="admin-tenant-selector">
              {tenants.map((tn) => (
                <button key={tn.slug} onClick={() => setTenant(tn.slug)} data-testid={`admin-tenant-${tn.slug}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tenant === tn.slug ? "bg-[#3F6E85] text-white shadow-sm" : "text-[#6E6B65] hover:text-[#3F6E85]"}`}>
                  {tn.logo_url && <img src={tn.logo_url} alt={tn.name} className={`h-4 object-contain ${tenant === tn.slug ? "bg-white rounded px-1" : ""}`} />}
                  {tn.name}{tn.is_active === false ? " ·off" : ""}
                </button>
              ))}
            </div>
          )}
          {!scoped && currentTenant && !currentTenant.is_default && (
            <Button onClick={() => openEdit(currentTenant)} data-testid="admin-edit-company-btn" variant="outline"
              className="rounded-full gap-2 h-9 border-[#3F6E85] text-[#3F6E85] hover:bg-[#3F6E85] hover:text-white">
              <Pencil className="w-4 h-4" /> {t("admin_edit_company")}
            </Button>
          )}
          {!scoped && (
            <Button onClick={openCreate} data-testid="admin-add-company-btn"
              className="rounded-full gap-2 bg-[#3F6E85] hover:bg-[#2B4A59] text-white h-9 ml-1">
              <Plus className="w-4 h-4" /> {t("admin_add_company")}
            </Button>
          )}
        </div>

        {!data ? (
          <p className="text-[#6E6B65]">...</p>
        ) : data.below_threshold ? (
          <>
            <h2 className="font-serif text-2xl tracking-tight text-[#2B4A59] mb-5">{currentTenant?.name || t("admin_company")}</h2>
            <InviteCodeBar tenant={currentTenant} onChanged={(code) => {
              setTenants((list) => list.map((x) => (x.slug === currentTenant.slug ? { ...x, invite_code: code } : x)));
              toast.success(`${t("admin_invite_code")}: ${code}`);
            }} />
            <div className="rounded-3xl p-8 bg-white border border-stone-200 text-[#6E6B65]" data-testid="admin-below-threshold">
              <ShieldCheck className="w-5 h-5 text-[#3F6E85] mb-3" />
              {t("admin_below_threshold").replace("{n}", data.min_group)}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl tracking-tight text-[#2B4A59]">
                {currentTenant?.name || t("admin_company")}
              </h2>
              <Button onClick={downloadCompanyPdf} disabled={pdfBusy} variant="outline" data-testid="admin-download-pdf-btn"
                className="rounded-full gap-2 border-[#3F6E85] text-[#3F6E85] hover:bg-[#3F6E85] hover:text-white">
                {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("admin_download_pdf")}
              </Button>
            </div>
            <InviteCodeBar tenant={currentTenant} onChanged={(code) => {
              setTenants((list) => list.map((x) => (x.slug === currentTenant.slug ? { ...x, invite_code: code } : x)));
              toast.success(`${t("admin_invite_code")}: ${code}`);
            }} />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
              <Metric icon={Users} label={t("total_employees")} value={data.total_employees} testid="metric-employees" />
              <Metric icon={Activity} label={t("active_users")} value={data.active_users} testid="metric-active" />
              <Metric icon={TrendingUp} label={t("completion_rate")} value={`${data.completion_rate}%`} testid="metric-completion" />
              <Metric icon={CheckCircle2} label={t("completed_week")} value={data.completed_week} testid="metric-completed" />
              <Metric icon={Star} label={t("avg_points")} value={data.avg_points} testid="metric-points" />
              <Metric icon={FileText} label={t("reports_generated")} value={data.reports_generated} testid="metric-reports" />
            </div>

            {!scoped && compare.length > 1 && (
              <div className="rounded-3xl p-8 bg-white border border-stone-200 mb-8" data-testid="admin-compare">
                <h2 className="font-serif text-2xl tracking-tight mb-1">{t("admin_compare_title")}</h2>
                <p className="text-sm text-[#6E6B65] mb-6">{t("admin_compare_sub")}</p>
                <div className="space-y-4">
                  {compare.map((c) => (
                    <div key={c.slug} data-testid={`compare-row-${c.slug}`} className="flex items-center gap-4">
                      <div className="w-40 flex items-center gap-2 shrink-0">
                        {c.logo_url && <img src={c.logo_url} alt={c.name} className="h-4 object-contain" />}
                        <span className="text-sm font-medium text-[#2B4A59] truncate">{c.name}</span>
                      </div>
                      <div className="flex-1 bg-[#F3F1ED] rounded-full h-5 overflow-hidden">
                        <div className="h-full rounded-full bg-[#3F6E85] flex items-center justify-end pr-2" style={{ width: `${Math.max(c.avg_participation, 4)}%` }}>
                          <span className="text-[10px] font-semibold text-white">{c.avg_participation}%</span>
                        </div>
                      </div>
                      <div className="w-52 shrink-0 text-xs text-[#6E6B65] flex gap-3 justify-end items-center">
                        <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /><b className="text-[#2B4A59]">{c.employees}</b></span>
                        <span className="inline-flex items-center gap-1"><Activity className="w-3.5 h-3.5" /><b className="text-[#2B4A59]">{c.active_users}</b></span>
                        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /><b className="text-[#2B4A59]">{c.completion_rate}%</b></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-3xl p-8 bg-white border border-stone-200" data-testid="admin-area-chart">
              <h2 className="font-serif text-2xl tracking-tight mb-6">{t("area_participation")}</h2>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6E6B65" }} />
                  <YAxis unit="%" tick={{ fontSize: 12, fill: "#6E6B65" }} />
                  <Tooltip cursor={{ fill: "#F3F1ED" }} formatter={(v) => `${v}%`} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {chartData.map((e, i) => (
                      <Cell key={i} fill={e.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top-3 problemi del cluster + trend miglioramento */}
            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <div className="rounded-3xl p-6 bg-white border border-stone-200" data-testid="admin-top-problems">
                <div className="flex items-center gap-2 mb-4 text-[#C81D6B]">
                  <AlertTriangle className="w-5 h-5" />
                  <h3 className="font-serif text-xl text-[#1A1D20]">{t("admin_top_problems")}</h3>
                </div>
                <div className="space-y-2">
                  {(data.top_3_problems || []).map((p, i) => (
                    <div key={p} className="flex items-center gap-3" data-testid={`admin-problem-${i}`}>
                      <span className="w-6 h-6 rounded-full bg-[#FBE4EF] text-[#C81D6B] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="font-medium text-[#2B4A59]">{t(p)}</span>
                      <span className="ml-auto text-sm text-[#6E6B65]">{data.area_participation[p]}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-3xl p-6 bg-[#3B604E] text-white" data-testid="admin-improvement">
                <div className="flex items-center gap-2 mb-3 text-[#bfe3d2]">
                  <TrendingUp className="w-5 h-5" />
                  <h3 className="font-serif text-xl">{t("admin_improvement")}</h3>
                </div>
                <p className="font-serif text-6xl leading-none">{data.improvement_rate ?? 0}%</p>
                <p className="text-sm text-white/70 mt-2">
                  {t("admin_improvement_sub").replace("{n}", data.improving_users ?? 0).replace("{m}", data.measured_users ?? 0)}
                </p>
              </div>
            </div>

            {!scoped && (<>
            {/* Admin: richieste di consulenza MDS MED */}
            <div className="rounded-3xl p-8 bg-white border border-stone-200 mt-8" data-testid="admin-consultations">
              <div className="flex items-center gap-2 mb-6 text-[#C81D6B]">
                <Stethoscope className="w-5 h-5" />
                <h2 className="font-serif text-2xl tracking-tight text-[#1A1D20]">{t("admin_consultations")}</h2>
                <span className="rounded-full bg-[#FBE4EF] text-[#C81D6B] text-xs font-bold px-2.5 py-0.5">{consults.length}</span>
              </div>
              {consults.length === 0 ? (
                <p className="text-sm text-[#6E6B65]" data-testid="admin-consultations-empty">{t("admin_consultations_empty")}</p>
              ) : (
                <div className="space-y-3">
                  {consults.map((c, i) => (
                    <div key={i} className="rounded-2xl border border-stone-200 p-4" data-testid={`admin-consultation-${i}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                        <span className="font-semibold text-[#2B4A59]">{c.user_name || c.user_email}</span>
                        <span className="text-xs text-[#6E6B65]">{(c.created_at || "").slice(0, 10)}</span>
                      </div>
                      <p className="text-xs text-[#6E6B65] mb-1">
                        {c.user_email}{c.phone ? ` · ${c.phone}` : ""}{c.topic ? ` · ${c.topic}` : ""}
                      </p>
                      <p className="text-sm text-[#3a3a3a]">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admin: report per profilo utente */}
            <div className="rounded-3xl p-8 bg-white border border-stone-200 mt-8" data-testid="admin-user-reports">
              <h2 className="font-serif text-2xl tracking-tight mb-6">{t("admin_user_reports")}</h2>

              <div className="flex flex-wrap items-end gap-3 mb-6" data-testid="admin-filters">
                <div className="flex items-center gap-2 text-[#3F6E85]">
                  <Filter className="w-4 h-4" />
                </div>
                <label className="flex flex-col text-xs text-[#6E6B65]">
                  {t("admin_filter_age_min")}
                  <input type="number" min="0" value={filters.min_age} data-testid="admin-filter-min-age"
                    onChange={(e) => setFilters((f) => ({ ...f, min_age: e.target.value }))}
                    className="mt-1 w-20 rounded-lg border border-stone-200 px-2 py-1.5 bg-white text-[#2B4A59]" />
                </label>
                <label className="flex flex-col text-xs text-[#6E6B65]">
                  {t("admin_filter_age_max")}
                  <input type="number" min="0" value={filters.max_age} data-testid="admin-filter-max-age"
                    onChange={(e) => setFilters((f) => ({ ...f, max_age: e.target.value }))}
                    className="mt-1 w-20 rounded-lg border border-stone-200 px-2 py-1.5 bg-white text-[#2B4A59]" />
                </label>
                <label className="flex flex-col text-xs text-[#6E6B65] flex-1 min-w-[180px]">
                  {t("admin_filter_pathology")}
                  <input type="text" value={filters.pathology} placeholder={t("admin_filter_pathology_ph")} data-testid="admin-filter-pathology"
                    onChange={(e) => setFilters((f) => ({ ...f, pathology: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") loadUsers(); }}
                    className="mt-1 rounded-lg border border-stone-200 px-3 py-1.5 bg-white text-[#2B4A59]" />
                </label>
                <button onClick={() => loadUsers()} data-testid="admin-filter-apply"
                  className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white text-sm font-medium px-5 py-2 transition-colors">
                  {t("admin_filter_apply")}
                </button>
                <button onClick={resetFilters} data-testid="admin-filter-reset"
                  className="rounded-full border border-stone-200 text-[#6E6B65] hover:bg-[#F3F1ED] text-sm px-4 py-2 flex items-center gap-1 transition-colors">
                  <X className="w-3.5 h-3.5" /> {t("admin_filter_reset")}
                </button>
              </div>

              <div className="space-y-3">
                {(users || []).length === 0 ? (
                  <p className="text-sm text-[#6E6B65] py-3" data-testid="admin-filter-empty">{t("admin_filter_empty")}</p>
                ) : (users || []).map((u) => {
                  const uOpen = openUser === u.id;
                  return (
                    <div key={u.id} className="rounded-2xl border border-stone-200 overflow-hidden">
                      <button onClick={() => { setOpenUser(uOpen ? null : u.id); setOpenReport(null); }} data-testid={`admin-user-${u.id}`}
                        className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-[#F3F1ED] transition-colors text-left">
                        <span>
                          <span className="font-semibold text-[#2B4A59]">{u.name || u.email}</span>
                          <span className="block text-xs text-[#6E6B65]">
                            {u.age ? `${u.age} ${lang === "it" ? "anni" : "y"} · ` : ""}
                            {(u.focus_areas || []).map((f) => t(f)).join(", ")}
                            {u.chronic_conditions ? ` · ${u.chronic_conditions}` : ""}
                            {u.pain_areas ? ` · ${u.pain_areas}` : ""}
                          </span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-xs rounded-full bg-[#EAF0EC] text-[#3F6E85] px-3 py-1">{u.reports.length}</span>
                          <ChevronDown className={`w-4 h-4 text-[#6E6B65] transition-transform ${uOpen ? "rotate-180" : ""}`} />
                        </span>
                      </button>
                      {uOpen && (
                        <div className="px-5 pb-4 border-t border-stone-100">
                          {/* Riassunto clinico AI */}
                          <div className="py-3" data-testid={`admin-clinical-${u.id}`}>
                            {!clinical[u.id] ? (
                              <button onClick={() => loadClinical(u.id)} data-testid={`admin-clinical-btn-${u.id}`}
                                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#3F6E85] hover:bg-[#2B4A59] text-white transition-colors">
                                <Sparkles className="w-4 h-4" /> {t("admin_clinical_generate")}
                              </button>
                            ) : clinical[u.id].loading ? (
                              <p className="text-sm text-[#6E6B65] flex items-center gap-2" data-testid={`admin-clinical-loading-${u.id}`}>
                                <span className="w-4 h-4 rounded-full border-2 border-[#3F6E85] border-t-transparent animate-spin" /> {t("admin_clinical_loading")}
                              </p>
                            ) : clinical[u.id].error ? (
                              <p className="text-sm text-[#B7410E]">{t("admin_clinical_error")}</p>
                            ) : (
                              <div className="rounded-2xl bg-[#EAF0EC] p-4" data-testid={`admin-clinical-content-${u.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs uppercase tracking-wider font-bold text-[#3B604E]">{t("admin_clinical_title")}</span>
                                  <button onClick={() => loadClinical(u.id, true)} className="text-xs text-[#3F6E85] flex items-center gap-1 hover:underline" data-testid={`admin-clinical-refresh-${u.id}`}>
                                    <RefreshCw className="w-3 h-3" /> {t("admin_clinical_refresh")}
                                  </button>
                                </div>
                                <MarkdownLite text={clinical[u.id].content} />
                              </div>
                            )}
                          </div>
                          {u.reports.length === 0 ? (
                            <p className="text-sm text-[#6E6B65] py-3">{t("admin_no_reports")}</p>
                          ) : (
                            u.reports.map((r) => (
                              <div key={r.id} className="py-2 border-b border-stone-50 last:border-0">
                                <button onClick={() => setOpenReport(openReport === r.id ? null : r.id)} data-testid={`admin-report-${r.id}`}
                                  className="flex items-center gap-2 text-sm text-[#3F6E85] font-medium hover:underline">
                                  {new Date(r.generated_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US")} · {r.status}
                                  {r.needs_specialist && <Stethoscope className="w-3.5 h-3.5 text-[#C81D6B]" />}
                                </button>
                                {openReport === r.id && r.status === "done" && (
                                  <div className="mt-2 rounded-xl bg-[#F3F1ED] p-4" data-testid={`admin-report-content-${r.id}`}>
                                    <MarkdownLite text={r.content} />
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </>)}
          </>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="admin-add-company-dialog">
          <DialogHeader>
            <DialogTitle>{editSlug ? t("admin_edit_company") : t("admin_add_company")}</DialogTitle>
            <DialogDescription>{t("admin_add_company_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label>{t("admin_company_name")}</Label>
              <Input data-testid="add-company-name" value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value, slug: editSlug ? f.slug : (f.slug || slugify(e.target.value)) }))}
                className="rounded-lg mt-1" placeholder="Es. Aquahome" />
            </div>
            <div>
              <Label>{t("admin_company_slug")}</Label>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-sm text-[#6E6B65]">riseyourlife.it/</span>
                <Input data-testid="add-company-slug" value={addForm.slug} disabled={!!editSlug}
                  onChange={(e) => setAddForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                  className="rounded-lg disabled:opacity-60" placeholder="aquahome" />
              </div>
              {slugReserved && <p role="alert" className="text-sm text-[#B7410E] mt-1" data-testid="add-company-slug-reserved">{t("admin_slug_reserved")}</p>}
            </div>
            <div>
              <Label>{t("admin_company_logo_url")}</Label>
              <Input data-testid="add-company-logo-url" value={addForm.logo_url.startsWith("data:") ? "" : addForm.logo_url}
                onChange={(e) => setAddForm((f) => ({ ...f, logo_url: e.target.value }))}
                className="rounded-lg mt-1" placeholder="https://..." />
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-[#6E6B65]">{t("admin_company_logo_or")}</span>
                <input type="file" accept="image/*" onChange={onLogoFile} data-testid="add-company-logo-file" className="text-xs" />
              </div>
              {addForm.logo_url && (
                <img src={addForm.logo_url} alt="preview" className="h-8 mt-3 object-contain" data-testid="add-company-logo-preview" />
              )}
            </div>
            <div className="flex items-center gap-4">
              <div>
                <Label>{t("admin_company_accent")}</Label>
                <input type="color" data-testid="add-company-accent" value={addForm.accent_color}
                  onChange={(e) => setAddForm((f) => ({ ...f, accent_color: e.target.value }))}
                  className="mt-1 h-10 w-16 rounded-lg border border-stone-200 cursor-pointer" />
              </div>
              <div className="flex-1">
                <Label>{t("admin_company_welcome")}</Label>
                <Textarea data-testid="add-company-welcome" value={addForm.welcome_message}
                  onChange={(e) => setAddForm((f) => ({ ...f, welcome_message: e.target.value }))}
                  className="rounded-lg mt-1 min-h-[44px]" placeholder={t("admin_company_welcome_ph")} />
              </div>
            </div>
            {editSlug && (
              <label className="flex items-center gap-3 pt-1" data-testid="add-company-active-row">
                <input type="checkbox" checked={addForm.is_active} data-testid="add-company-active"
                  onChange={(e) => setAddForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[#3F6E85]" />
                <span className="text-sm text-[#6E6B65]">{t("admin_company_active")}</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} data-testid="add-company-cancel">{t("cancel")}</Button>
            <Button onClick={saveTenant} disabled={addBusy || !addForm.name.trim()} data-testid="add-company-submit"
              className="bg-[#3F6E85] hover:bg-[#2B4A59] gap-2">
              {addBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editSlug ? t("admin_save_company") : t("admin_create_company")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* hidden aggregated PDF report for the selected company */}
      <div style={{ position: "fixed", left: -99999, top: 0, width: 794 }}>
        {data && !data.below_threshold && (
          <div ref={pdfRef} style={{ width: 794, padding: 48, background: "#ffffff", fontFamily: "Arial, sans-serif", color: "#2B4A59", boxSizing: "border-box" }} data-testid="admin-pdf-report">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "3px solid #3F6E85", paddingBottom: 16, marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 13, letterSpacing: 2, textTransform: "uppercase", color: "#6E6B65" }}>RISE your LIFE · Report Aziendale</div>
                <div style={{ fontSize: 30, fontWeight: 700, marginTop: 4 }}>{currentTenant?.name || tenant}</div>
                <div style={{ fontSize: 12, color: "#9c9c9c", marginTop: 4 }}>{new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</div>
              </div>
              {currentTenant?.logo_url ? (
                <img src={currentTenant.logo_url} alt={currentTenant.name} crossOrigin="anonymous" style={{ height: 44, objectFit: "contain" }} />
              ) : null}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 26 }}>
              {[
                [t("total_employees"), data.total_employees],
                [t("active_users"), data.active_users],
                [t("completion_rate"), `${data.completion_rate}%`],
                [t("completed_week"), data.completed_week],
                [t("avg_points"), data.avg_points],
                [t("reports_generated"), data.reports_generated],
              ].map(([lab, val], i) => (
                <div key={i} style={{ border: "1px solid #ece9e3", borderRadius: 14, padding: "14px 16px", background: "#FBFAF7" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#2B4A59" }}>{val}</div>
                  <div style={{ fontSize: 12, color: "#6E6B65", marginTop: 2 }}>{lab}</div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{t("area_participation")}</div>
            <div style={{ marginBottom: 26 }}>
              {Object.keys(AREA_META).map((a) => {
                const v = data.area_participation[a] || 0;
                return (
                  <div key={a} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 9 }}>
                    <div style={{ width: 140, fontSize: 13 }}>{t(a)}</div>
                    <div style={{ flex: 1, background: "#F3F1ED", borderRadius: 8, height: 14, overflow: "hidden" }}>
                      <div style={{ width: `${v}%`, height: "100%", background: AREA_META[a].color, borderRadius: 8 }} />
                    </div>
                    <div style={{ width: 42, textAlign: "right", fontSize: 13, fontWeight: 700 }}>{v}%</div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={{ border: "1px solid #ece9e3", borderRadius: 14, padding: 16, background: "#FBFAF7" }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#B7410E" }}>{t("top_problems") || "Aree critiche"}</div>
                <div style={{ fontSize: 13 }}>{(data.top_3_problems || []).map((a) => t(a)).join(" · ") || "—"}</div>
              </div>
              <div style={{ border: "1px solid #ece9e3", borderRadius: 14, padding: 16, background: "#FBFAF7" }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#3B604E" }}>{t("improvement_rate") || "Tasso di miglioramento"}</div>
                <div style={{ fontSize: 13 }}>{data.improvement_rate}% ({data.improving_users}/{data.measured_users})</div>
              </div>
            </div>

            <div style={{ fontSize: 11, fontStyle: "italic", color: "#9c9c9c", borderTop: "1px solid #ece9e3", paddingTop: 14, marginTop: 24 }}>
              Dati aggregati e anonimizzati · RISE your LIFE
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl p-5 bg-white border border-stone-200" data-testid={testid}>
    <Icon className="w-5 h-5 mb-3 text-[#3F6E85]" />
    <p className="font-serif text-3xl text-[#2B4A59]">{value}</p>
    <p className="text-xs text-[#6E6B65] leading-tight mt-1">{label}</p>
  </div>
);
